/**
 * The one way a page button hands a request to the Innovate Assistant (task
 * 1200, lifted from task 1196's `SuggestCriteriaButton`).
 *
 * The click IS the request: `handoff()` opens the Assistant sidebar, sends
 * `prompt` as the User's own first turn of a NEW thread through
 * `seedFirstTurn` (task 1117), and asks the composer to pulse once
 * (`pulseComposer`). So the User sees the request travel, `sent` holds true
 * for `SENT_STATE_MS`; the button renders its sent state (check icon, "Sent to
 * Assistant", disabled) from it.
 *
 * A click is ignored while `sent`, and while `assistant.isSending`: a turn
 * already in flight would make `seedFirstTurn` a no-op, and claiming "sent"
 * then would be untrue. The guard reads a ref, because a second click can
 * arrive before the re-render that disables the button.
 *
 * `assistant` is `undefined` with no Assistant provider mounted; callers render
 * nothing then (the `CreateInterfaceButton` rule).
 *
 * `autoSubmit: false` is the draft arm (task 1215): `handoff()` opens the
 * Assistant with `prompt` placed in the composer through `openWithDraft`,
 * unsent, with no sent state and no pulse — so no LLM turn is spent on one
 * click. The hook does not read a flag itself: each caller reads
 * `innovate.fe.testing.assistant-handoff` and passes it here, so the lever
 * stays visible at the button that spends the turn.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOptionalAssistant } from '@/providers/AssistantProvider';

/** How long a handoff button holds its sent state (task 1196). */
export const SENT_STATE_MS = 1000;

export interface AssistantHandoffOptions {
  /** True (the default) sends the turn; false places it in the composer as a draft. */
  autoSubmit?: boolean;
}

export function useAssistantHandoff(prompt: string, { autoSubmit = true }: AssistantHandoffOptions = {}) {
  const assistant = useOptionalAssistant();
  const [sent, setSent] = useState(false);
  const sentRef = useRef(false);

  useEffect(() => {
    if (!sent) return;
    const timer = window.setTimeout(() => {
      sentRef.current = false;
      setSent(false);
    }, SENT_STATE_MS);
    return () => window.clearTimeout(timer);
  }, [sent]);

  const handoff = useCallback(() => {
    if (!assistant) return;
    if (!autoSubmit) {
      assistant.openWithDraft(prompt);
      return;
    }
    if (sentRef.current || assistant.isSending) return;
    sentRef.current = true;
    setSent(true);
    assistant.open();
    void assistant.seedFirstTurn(prompt);
    assistant.pulseComposer();
  }, [assistant, prompt, autoSubmit]);

  return { assistant, sent, handoff };
}

export default useAssistantHandoff;
